import type { ArticleDetail, SavePayload } from '../api/client';
import type { SeoState } from '../components/SeoPanel';
import { buildPublicUrl } from '../lib/url';

export interface EditorState {
  title: string;
  excerpt: string;
  status: string;
  scheduledAt: number | null;
  bodyJson: string | null;
  seo: SeoState;
  relatedIds: string[];
  categoryIds: string[];
  tags: string[];
  coverMediaId: string | null;
  coverUrl: string | null;
}

export const EMPTY: EditorState = {
  title: '',
  excerpt: '',
  status: 'draft',
  scheduledAt: null,
  bodyJson: null,
  seo: {},
  relatedIds: [],
  categoryIds: [],
  tags: [],
  coverMediaId: null,
  coverUrl: null,
};

/** 由詳情 API 回應建立編輯狀態。 */
export function hydrateState(d: ArticleDetail): EditorState {
  return {
    title: d.article.title,
    excerpt: d.article.excerpt ?? '',
    status: normalizeStatus(d.article.status),
    scheduledAt: toMs(d.article.scheduledAt),
    bodyJson: d.article.bodyJson,
    seo: (d.seo ?? {}) as SeoState,
    relatedIds: d.relatedIds,
    categoryIds: d.categoryIds,
    tags: d.tags,
    coverMediaId: d.article.coverMediaId ?? null,
    coverUrl: d.coverUrl ?? null,
  };
}

/** API 的時間欄位經 JSON 後是 ISO 字串（Drizzle timestamp → Date → string），統一轉成毫秒。 */
export function toMs(v: unknown): number | null {
  if (v == null || v === '') return null;
  const ms = typeof v === 'number' ? v : new Date(v as string).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** 舊資料的 in_review（審稿中）已不再使用，一律視為草稿。 */
export const normalizeStatus = (status: string): string =>
  status === 'in_review' ? 'draft' : status;

/**
 * 組出 PUT /articles/:id 的「內容」payload（手動與自動儲存共用）。
 * 刻意不含 status / scheduledAt：狀態只能由「立即發佈」「確認排程」等明確按鈕變更，
 * 自動儲存永遠不會翻動狀態。
 */
export function payloadOf(s: EditorState): SavePayload {
  return {
    patch: {
      title: s.title,
      excerpt: s.excerpt,
      bodyJson: s.bodyJson,
      coverMediaId: s.coverMediaId,
    },
    seo: s.seo as Record<string, unknown>,
    relatedIds: s.relatedIds,
    categoryIds: s.categoryIds,
    tags: s.tags,
  };
}

/** 內容 payload 再加上「排程刊登」狀態（僅「確認排程」按鈕使用）。 */
export function withSchedule(p: SavePayload, scheduledAt: number): SavePayload {
  return { ...p, patch: { ...p.patch, status: 'scheduled', scheduledAt } };
}

/** 已發佈才給正式連結（PUBLIC_WEB_URL/<slug>，百分比編碼），否則 null。 */
export function publicUrlOf(d: ArticleDetail | undefined, status: string): string | null {
  const slug = d?.article.slug;
  const base = d?.webBaseUrl;
  return status === 'published' && slug && base ? buildPublicUrl(base, slug) : null;
}
